// src/components/EvaluationConfigurationLayout.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import useStore from '../config/store';
import EvaluationConfigurationStepOne from './EvaluationConfigurationStepOne';
import EvaluationConfigurationStepTwo from './EvaluationConfigurationStepTwo';
import EvaluationConfigurationStepThree from './EvaluationConfigurationStepThree';
import EvaluationConfigurationStepFour from './EvaluationConfigurationStepFour';
import { ASSESSMENT_MODES } from './assessmentMode';
import { useTranslation } from 'react-i18next';

const StepperContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  backgroundColor: '#f8f9fa',
  borderRadius: theme.spacing(1),
}));

const EvaluationConfigurationLayout = () => {
  // Estados principales
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [competenciesConfig, setCompetenciesConfig] = useState({
    capabilities: [],
    isValid: false,
    totalPercentage: 0
  });
  const [candidatesConfig, setCandidatesConfig] = useState({
    candidates: [],
    isValid: false,
    totalSelected: 0
  });

  // Estado para datos del evaluation
  const [evaluationData, setEvaluationData] = useState({
    title: '',
    description: '',
    timeLimit: 15,
    mode: ASSESSMENT_MODES.COMPLETE, // simple / completo (PLAN_UNIFICACION_ASSESSMENT.md)
    isValid: false
  });

  const { showAlert } = useStore.getState();
  const { t } = useTranslation();

  // Pasos del formulario (ORIGINAL - sin cambios)
  const steps = t('evaluations.evaluationSteps', { returnObjects: true });

  // Funciones de manejo de estados
  const handlePositionSelect = (position) => {
    setSelectedPosition(position);
  };

  const handleCompetenciesChange = (config) => {
    setCompetenciesConfig(config);
    
    if (config.totalTime && config.totalTime > 0) {
      setEvaluationData(prev => ({
        ...prev,
        timeLimit: config.totalTime
      }));
    }
  };

  const handleCandidatesChange = (config) => {
    setCandidatesConfig(config);
  };

  const handleEvaluationDataChange = (data) => {
    const isValid = data.title && data.title.trim() !== '';
    // Cambiar el modo cambia qué postulantes se pueden agregar (con o sin
    // psicolaboral): se limpian los ya elegidos (PLAN_UNIFICACION_ASSESSMENT.md).
    if ((data.mode || ASSESSMENT_MODES.COMPLETE) !== (evaluationData.mode || ASSESSMENT_MODES.COMPLETE)) {
      setCandidatesConfig({
        candidates: [],
        isValid: false,
        totalSelected: 0
      });
    }
    setEvaluationData({ ...data, isValid });
  };

  const handleResetToStep1 = () => {
    // Resetear todos los estados
    setActiveStep(0);
    setSelectedPosition(null);
    setCompetenciesConfig({
      capabilities: [],
      isValid: false,
      totalPercentage: 0
    });
    setCandidatesConfig({
      candidates: [],
      isValid: false,
      totalSelected: 0
    });
    setEvaluationData({
      title: '',
      description: '',
      timeLimit: 15,
      mode: ASSESSMENT_MODES.COMPLETE,
      isValid: false
    });

    // Mostrar mensaje de confirmación
    showAlert(t('evaluations.resetToStep1Success'), 'success');
  };

  // Función para avanzar al siguiente paso
  const handleNext = () => {
    if (activeStep === 0 && (!selectedPosition || !evaluationData.isValid)) {
      if (!selectedPosition) {
        showAlert(t('evaluations.pleaseSelectPosition'), 'warning');
      } else if (!evaluationData.isValid) {
        showAlert(t('evaluations.pleaseFillTitle'), 'warning');
      }
      return;
    }
    
    if (activeStep === 1 && !competenciesConfig.isValid) {
      if (competenciesConfig.capabilities.length === 0) {
        showAlert(t('evaluations.pleaseFillCapability'), 'warning');
      } else if (competenciesConfig.totalPercentage !== 100) {
        showAlert(t('evaluations.percentageError'), 'warning');
      }
      return;
    }

    if (activeStep === 2 && !candidatesConfig.isValid) {
      showAlert(t('evaluations.pleaseSelectApplicant'), 'warning');
      return;
    }

    // Avanzar al siguiente paso
    setActiveStep(prevStep => prevStep + 1);
  };

  // Función para retroceder
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  // Verificar si se puede continuar en cada paso
  const canContinue = () => {
    switch (activeStep) {
      case 0:
        return selectedPosition && evaluationData.isValid;
      case 1:
        return competenciesConfig.isValid;
      case 2:
        return candidatesConfig.isValid && candidatesConfig.candidates.length >= 1;
      case 3:
        return false; // En el último paso no hay botón "Siguiente"
      default:
        return true;
    }
  };

  // Renderizar el contenido según el paso activo
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <EvaluationConfigurationStepOne 
            selectedPosition={selectedPosition}
            onPositionSelect={handlePositionSelect}
            evaluationData={evaluationData}
            onEvaluationChange={handleEvaluationDataChange}
          />
        );
      case 1:
        return (
          <EvaluationConfigurationStepTwo 
            selectedPosition={selectedPosition}
            competencies={competenciesConfig}
            onCompetenciesChange={handleCompetenciesChange}
          />
        );
      case 2:
        return (
          <EvaluationConfigurationStepThree 
            selectedPosition={selectedPosition}
            competenciesConfig={competenciesConfig}
            candidatesConfig={candidatesConfig}
            onCandidatesChange={handleCandidatesChange}
            mode={evaluationData.mode}
          />
        );
      case 3:
        return (
          <EvaluationConfigurationStepFour 
            selectedPosition={selectedPosition}
            competenciesConfig={competenciesConfig}
            candidatesConfig={candidatesConfig}
            evaluationData={evaluationData}
            onResetToStep1={handleResetToStep1}
          />
        );
      default:
        return <Typography>{t('evaluations.unknownStep')}</Typography>;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        {t('evaluations.configurationTitle')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('evaluations.configurationSubTitle')}
      </Typography>

      {/* Stepper */}
      <StepperContainer>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </StepperContainer>

      {/* Contenido del paso */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {renderStepContent()}
      </Paper>

      {/* Botones de navegación */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          {t('back')}
        </Button>
        
        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canContinue()}
            endIcon={<ArrowForwardIcon />}
          >
            {t('next')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EvaluationConfigurationLayout;